import classes from './buildingSkeleton.module.css';

/**
 * Shared building-silhouette skeleton. Generic skyscraper outline:
 * floor-number gutter on the left, an alert-badge column further
 * left of it, a slim centred band whose side-borders form the tower
 * walls and bottom-borders form the floor dividers, sparse chips
 * inside, and one alert glyph mid-tower.
 *
 * Two sizing strategies via the `flexFill` prop:
 *
 *  - Default (`flexFill: false`): each `.floorRow` is a fixed `30px`
 *    tall — gives the Monitor's existing `BuildingViewSvg` loading
 *    state its familiar ~600px tower. Use this when the parent has
 *    no constrained height.
 *
 *  - `flexFill: true`: each `.floorRow` is `flex: 1 1 0` with a low
 *    `min-height`, so the stack distributes across whatever vertical
 *    space the parent gives it. Use this inside locked-height
 *    containers (e.g. the Dashboard's 540px Visual Site Overview
 *    card body) where a fixed 600px tower would overflow.
 *
 * Identical visual treatment either way — the only difference is the
 * row-sizing track. The skeleton owns its own outer wrapper so the
 * consumer can drop it directly into a flex column body without
 * needing any height-propagation tricks.
 */
export function BuildingSkeleton({ flexFill = false }: { flexFill?: boolean }) {
  /* CSS-module class lookups are `string | undefined` under
     `noUncheckedIndexedAccess`; coerce to string so the JSX className
     props never see `undefined`. */
  const tower = classes.tower ?? '';
  const stack = classes.floorStack ?? '';
  const row   = classes.floorRow ?? '';
  const towerCls = flexFill ? `${tower} ${classes.towerFlexFill ?? ''}` : tower;
  const stackCls = flexFill ? `${stack} ${classes.floorStackFlexFill ?? ''}` : stack;
  const rowCls   = flexFill ? `${row} ${classes.floorRowFlexFill ?? ''}`     : row;

  return (
    <div className={towerCls} aria-hidden="true">
      <div className={stackCls}>
        {Array.from({ length: FLOOR_COUNT }).map((_, i) => (
          <FloorRow key={i} index={i} rowCls={rowCls} />
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Floor pattern data
// ──────────────────────────────────────────────────────────────────

const FLOOR_COUNT = 20;

/* Sparse chip layout per floor — empty arrays render as plain band,
   adding rhythmic breathing room as the eye travels down the tower.
   Three "tones" (a/b/c) cycle the chip colour subtly so the stack
   doesn't read as a striped barcode.

   Widths are percentages of the band so the same pattern fits both
   the wide Monitor card (band ≈ 270px) and the half-width Dashboard
   card (band ≈ 88px). Pixel widths would overflow the narrower band
   and get clipped by `overflow: hidden`. */
const SLAB_CHIP_PATTERN: Array<Array<{ w: string; tone: 'a' | 'b' | 'c' }>> = [
  [],
  [{ w: '40%', tone: 'a' }],
  [],
  [{ w: '32%', tone: 'b' }, { w: '46%', tone: 'a' }],
  [{ w: '48%', tone: 'a' }, { w: '28%', tone: 'c' }],
  [],
  [{ w: '34%', tone: 'b' }, { w: '24%', tone: 'a' }, { w: '18%', tone: 'c' }],
  [{ w: '38%', tone: 'a' }, { w: '46%', tone: 'b' }],
  [],
  [{ w: '24%', tone: 'a' }, { w: '36%', tone: 'b' }, { w: '20%', tone: 'c' }],
  [{ w: '44%', tone: 'b' }, { w: '36%', tone: 'a' }],
  [{ w: '48%', tone: 'a' }, { w: '30%', tone: 'b' }],
  [],
  [{ w: '32%', tone: 'b' }, { w: '26%', tone: 'a' }, { w: '20%', tone: 'c' }],
  [{ w: '30%', tone: 'a' }, { w: '40%', tone: 'b' }, { w: '18%', tone: 'c' }],
  [{ w: '40%', tone: 'a' }, { w: '30%', tone: 'b' }, { w: '22%', tone: 'c' }],
  [],
  [{ w: '46%', tone: 'b' }, { w: '28%', tone: 'a' }],
  [{ w: '36%', tone: 'a' }, { w: '40%', tone: 'b' }, { w: '20%', tone: 'c' }],
  [{ w: '32%', tone: 'b' }, { w: '42%', tone: 'a' }],
];

/* One alert badge anchored mid-tower — mirrors `FloorAlertBadge`'s
   side placement on the live BuildingViewSvg. */
const ALERT_FLOOR_INDEX = 10;

function FloorRow({ index, rowCls }: { index: number; rowCls: string }) {
  const chips = SLAB_CHIP_PATTERN[index] ?? [];
  const showAlert = index === ALERT_FLOOR_INDEX;
  return (
    <div className={rowCls}>
      {/* Outer-left badge gutter, matching FloorAlertBadge anchor. */}
      <div className={classes.floorBadgeCol}>
        {showAlert && <span className={`${classes.shimmer} ${classes.floorBadge}`} />}
      </div>
      <span className={`${classes.shimmer} ${classes.floorNumber}`} />
      <div className={classes.floorBand}>
        <div className={classes.floorChipRow}>
          {chips.map((c, i) => (
            <span
              key={i}
              className={`${classes.shimmer} ${classes.floorChip} ${classes[`tone_${c.tone}`] ?? ''}`}
              style={{ width: c.w }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
